import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface ProdutoType {
    id: number;
    name: string;
    description?: string;
    value?: number;
}

export interface CreateProdutoType {
    name: string;
    description?: string;
    value?: number;
    empresaId: number;
}

export interface UpdateProdutoType {
    id: number;
    name: string;
    description?: string;
    value?: number;
}


export default function useProduto() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listProduto, setListProduto] = useState<ProdutoType[]>([]);
    const [listProdutoByEmpresa, setListProdutoByEmpresa] = useState<ProdutoType[]>([]);
    const [produtoPageCount, setProdutoPageCount] = useState<number>(0);
    const [initialListProduto, setInitialListProduto] = useState<ProdutoType[]>([]);
    const [produto, setProduto] = useState<ProdutoType>({
        id: 0,
        name: "",
        description: "",
        value: 0,
    });

    function createProduto(createProdutoType: CreateProdutoType): Promise<CreateProdutoType | null> {
        setLoading(true);
        return api
            .post(`/Produto/create`, createProdutoType)
            .then((response) => {
                const createdProduto: CreateProdutoType = response.data;
                showToast("success", "Produto criada com sucesso.");
                setLoading(false);
                return createdProduto;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar produto. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar produto:", error);
                return null;
            });
    }

    function getAllProdutoPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
    ): Promise<ProdutoType[] | null> {
        setLoading(true);
        let url = `/Produto/getAllProdutoPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListProduto(response.data.result);
                setInitialListProduto(response.data.result);
                setProdutoPageCount(response.data.pageCount);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar produtso. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar produtos:", error);
                return null;
            });
    }

    function updateProduto(updateProdutoData: UpdateProdutoType): Promise<UpdateProdutoType | null> {
        setLoading(true);
        return api
            .put(`/Produto/${updateProdutoData.id}`, updateProdutoData)
            .then((response) => {
                showToast("success", "Produto atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar produto. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar produto:", error);
                return null;
            });
    }

    function deleteProduto(produtoId: number): Promise<ProdutoType | null> {
        setLoading(true);
        return api
            .delete(`/Produto/${produtoId}`)
            .then(() => {
                setProduto({
                    id: 0,
                    name: "",
                    description: "",
                    value: 0,
                });
                showToast("success", "Produto deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar produto. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar produto:", error);
                return null;
            });
    }

    function handleGetProdutoByEmpresaId(empresaId: number): Promise<ProdutoType[] | null> {
        setLoading(true);
        return api
            .get(`/Produto/getByEmpresaId/${empresaId}`)
            .then((response) => {
                const produto: ProdutoType[] = response.data;
                setListProdutoByEmpresa(produto);
                setLoading(false);
                return produto;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações dos produtos. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações dos produtos:", error);
                return null;
            });
    }

    return {
        createProduto,
        getAllProdutoPaged,
        updateProduto,
        deleteProduto,
        setListProduto,
        listProduto,
        setProdutoPageCount,
        produtoPageCount,
        setInitialListProduto,
        initialListProduto,
        setProduto,
        produto,
        handleGetProdutoByEmpresaId,
        setListProdutoByEmpresa,
        listProdutoByEmpresa,
    };
}